import React, { useEffect, useRef, useState } from "react";
import {
  Server,
  Cloud,
  Plus,
  Trash2,
  Edit2,
  Loader,
  X,
  MoreVertical,
  Globe,
  Plug,
  Shield,
  Layers,
  Box,
  Activity,
  CalendarDays,
} from "lucide-react";
import {
  AppComponentDeploymentItem,
  AppComponentMappingResponse,
} from "../../../../api/frameworksApi";
import { StatusBadge } from "../cmdbUtils";
import { Tip } from "../../../components/ui/tooltip";

interface AppComponentTabProps {
  components: AppComponentMappingResponse[];
  onRemoveComponent: (comp: AppComponentMappingResponse) => void;
  onAddDevice: (comp: AppComponentMappingResponse) => void;
  onAddCloudResource: (comp: AppComponentMappingResponse) => void;
  onEditComponent?: (comp: AppComponentMappingResponse) => void;
  onEditDeployment: (d: any) => void;
  onRemoveDeployment: (deployment: AppComponentDeploymentItem) => void;
  removingCompIds: Record<string, boolean>;
  removingDeploymentIds: Record<string, boolean>;
}

// ── Helpers ────────────────────────────────────────────────────────────────

function typeIconBg(componentType?: string | null, source?: string): string {
  if (source === "CLOUD")
    return "bg-indigo-500/20 text-indigo-600 dark:text-indigo-400";
  if (source === "SERVICE")
    return "bg-blue-500/20 text-blue-600 dark:text-blue-400";
  const t = (componentType || "").toUpperCase();
  if (t === "OPERATING_SYSTEM")
    return "bg-violet-500/20 text-violet-600 dark:text-violet-400";
  if (t === "DATABASE")
    return "bg-amber-500/20 text-amber-600 dark:text-amber-400";
  if (t === "MIDDLEWARE")
    return "bg-emerald-500/20 text-emerald-600 dark:text-emerald-400";
  if (t === "APPLICATION")
    return "bg-pink-500/20 text-pink-600 dark:text-pink-400";
  return "bg-slate-500/20 text-slate-600 dark:text-slate-400";
}

function getEffectiveSource(comp: AppComponentMappingResponse): string {
  const deps = comp.deployments ?? [];
  const hasOnPrem = deps.some((d) => d.ciType !== "CLOUD");
  const hasCloud = deps.some((d) => d.ciType === "CLOUD");
  if (hasOnPrem && hasCloud) return "MIXED";
  if (hasCloud || comp.source === "CLOUD") return "CLOUD";
  if (comp.source === "SERVICE") return "SERVICE";
  return "ON_PREM";
}

function sourceBadgeCls(label: string): string {
  if (label === "CLOUD")
    return "bg-indigo-500/15 text-indigo-600 dark:text-indigo-400 border border-indigo-500/40";
  if (label === "SERVICE")
    return "bg-blue-500/15 text-blue-600 dark:text-blue-400 border border-blue-500/40";
  if (label === "MIXED")
    return "bg-violet-500/15 text-violet-600 dark:text-violet-400 border border-violet-500/40";
  return "bg-slate-100 dark:bg-slate-500/10 text-slate-600 dark:text-slate-300 border border-slate-300 dark:border-slate-500/50";
}

function rolePillCls(role?: string | null): string {
  const r = (role || "").toUpperCase();
  if (r === "PRIMARY") return "bg-indigo-600 text-white";
  if (r === "BACKUP") return "bg-slate-500/30 text-slate-300";
  if (r === "STANDBY") return "bg-purple-500/20 text-purple-300";
  if (r === "READ_REPLICA") return "bg-cyan-500/20 text-cyan-300";
  if (r === "DR") return "bg-rose-500/20 text-rose-300";
  return "bg-indigo-600/80 text-white";
}

function aggregateStatus(deployments: any[]): string | null {
  if (!deployments.length) return null;
  if (deployments.some((d) => d.status?.toUpperCase() === "RUNNING"))
    return "RUNNING";
  return deployments[0]?.status ?? null;
}

// ── Sub-components ─────────────────────────────────────────────────────────

function StatusDot({ status }: { status?: string | null }) {
  const s = (status || "").toUpperCase();
  const dot =
    s === "RUNNING"
      ? "bg-emerald-500"
      : s === "STOPPED"
        ? "bg-red-500"
        : s === "DEGRADED"
          ? "bg-orange-500"
          : "bg-slate-500";
  const text =
    s === "RUNNING"
      ? "text-emerald-400"
      : s === "STOPPED"
        ? "text-red-400"
        : s === "DEGRADED"
          ? "text-orange-400"
          : "text-slate-400";
  const label = status
    ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
    : "Unknown";
  return (
    <div className={`flex items-center gap-1.5 ${text}`}>
      <span className="relative flex size-2 flex-shrink-0">
        {s === "RUNNING" && (
          <span
            className={`absolute inline-flex size-full rounded-full ${dot} opacity-50 animate-ping`}
          />
        )}
        <span className={`relative inline-flex size-2 rounded-full ${dot}`} />
      </span>
      <span className="text-xs font-semibold">{label}</span>
    </div>
  );
}

function DetailRow({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value?: string | number | null;
}) {
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-border/50 last:border-0">
      <div className="flex items-center gap-2.5 text-foreground">
        {icon}
        <span className="text-xs font-medium">{label}</span>
      </div>
      <span className="text-xs font-semibold text-foreground">
        {value || "—"}
      </span>
    </div>
  );
}

function DeviceField({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value?: string | number | null;
}) {
  return (
    <div className="flex items-start justify-between gap-3 py-2 border-b border-border/50 last:border-0">
      <div className="flex items-center gap-2 d flex-shrink-0">
        {icon}
        <span className="text-xs font-medium text-foreground">{label}</span>
      </div>
      <span className="text-xs font-semibold text-foreground text-right break-all min-w-0">
        {value ?? "—"}
      </span>
    </div>
  );
}

function MoreMenu({
  onRemove,
  removing,
}: {
  onRemove: () => void;
  removing?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function h(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    }
    if (open) document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);
  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="p-1.5 rounded-lg hover:bg-muted transition-colors d"
      >
        {removing ? (
          <Loader className="size-4 animate-spin" />
        ) : (
          <MoreVertical className="size-4" />
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-8 z-20 bg-popover border border-border rounded-lg shadow-lg py-1 min-w-[140px]">
          <button
            onClick={() => {
              onRemove();
              setOpen(false);
            }}
            className="w-full text-left px-3 py-2 text-sm text-red-400 hover:bg-red-500/10 flex items-center gap-2 transition-colors"
          >
            <Trash2 className="size-3.5" /> Remove
          </button>
        </div>
      )}
    </div>
  );
}

// ── Inline Detail Panel ─────────────────────────────────────────────────────

function ComponentDetailPanel({
  comp,
  onClose,
  onAddDevice,
  onAddCloudResource,
  onEditComponent,
  onEditDeployment,
  onRemoveDeployment,
  removingDeploymentIds,
}: {
  comp: AppComponentMappingResponse;
  onClose: () => void;
  onAddDevice: (c: AppComponentMappingResponse) => void;
  onAddCloudResource: (c: AppComponentMappingResponse) => void;
  onEditComponent?: (c: AppComponentMappingResponse) => void;
  onEditDeployment: (d: any) => void;
  onRemoveDeployment: (d: AppComponentDeploymentItem) => void;
  removingDeploymentIds: Record<string, boolean>;
}) {
  const deployments = comp.deployments ?? [];
  const componentStatus = aggregateStatus(deployments);
  const isCloud = comp.source === "CLOUD";
  const isService = comp.source === "SERVICE";
  const effectiveSrc = getEffectiveSource(comp);
  const iconClass = typeIconBg(comp.component.componentType, comp.source);

  return (
    <div className="flex flex-col flex-1 min-h-0 bg-card rounded-2xl border border-border overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3.5 border-b border-border flex-shrink-0">
        <div
          className={`flex-shrink-0 size-8 rounded-xl flex items-center justify-center ${iconClass}`}
        >
          {isCloud ? (
            <Cloud className="size-4" />
          ) : (
            <Server className="size-4" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-bold text-sm text-foreground truncate">
              {comp.component.title}
            </span>
            {comp.component.componentType && (
              <span className="text-[10px] font-semibold bg-muted d px-2 py-0.5 rounded-md tracking-wide">
                {comp.component.componentType}
              </span>
            )}
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-1.5 rounded-lg hover:bg-muted transition-colors d flex-shrink-0"
        >
          <X className="size-4" />
        </button>
      </div>

      {/* Content */}
      <div className="p-4 space-y-4 flex-1 min-h-0 overflow-y-auto">
        {/* Component Summary */}
        <div className="rounded-xl border border-border bg-muted/10 overflow-hidden">
          <div className="relative grid grid-cols-2 sm:grid-cols-3 min-[1700px]:grid-cols-6 divide-x divide-y min-[1700px]:divide-y-0 divide-border sm:after:block after:hidden min-[1700px]:after:hidden after:absolute after:inset-x-0 after:top-1/2 after:h-px after:bg-border after:pointer-events-none">
            <div className="p-3 text-center">
              <div className="flex items-center justify-center gap-1 text-foreground mb-1.5">
                <Layers className="size-3" />
                <span className="text-[10px] font-medium whitespace-nowrap">Environment</span>
              </div>
              <span
                className={`text-[10px] font-bold px-2 py-0.5 rounded-lg tracking-wide ${sourceBadgeCls(effectiveSrc)}`}
              >
                {effectiveSrc}
              </span>
            </div>
            <div className="p-3 text-center">
              <div className="flex items-center justify-center gap-1 text-foreground mb-1.5">
                <Box className="size-3" />
                <span className="text-[10px] font-medium whitespace-nowrap">Assets</span>
              </div>
              <span className="text-lg font-bold text-foreground">
                {deployments.length}
              </span>
            </div>
            <div className="p-3 text-center">
              <div className="flex items-center justify-center gap-1 text-foreground mb-1.5">
                <Activity className="size-3" />
                <span className="text-[10px] font-medium whitespace-nowrap">Status</span>
              </div>
              {componentStatus ? (
                <StatusBadge status={componentStatus} />
              ) : (
                <span className="text-xs d">—</span>
              )}
            </div>
            <div className="p-3 text-center">
              <div className="flex items-center justify-center gap-1 text-foreground mb-1.5">
                <Layers className="size-3" />
                <span className="text-[10px] font-medium whitespace-nowrap">Type</span>
              </div>
              <span className="text-[10px] font-bold text-foreground ">
                {comp.component.componentType || "—"}
              </span>
            </div>
            <div className="p-3 text-center">
              <div className="flex items-center justify-center gap-1 text-foreground mb-1.5">
                <Layers className="size-3 flex-shrink-0" />
                <span className="text-[10px] font-medium whitespace-nowrap">
                  Component Layer
                </span>
              </div>
              <div className="flex items-center justify-center gap-1.5">
                <span className="text-[10px] font-bold text-foreground">
                  {comp.sspLayerName || "—"}
                </span>
                {onEditComponent && (
                  <Tip label="Edit Component Layer" side="bottom">
                    <button
                      onClick={() => onEditComponent(comp)}
                      className="p-1 rounded-md hover:bg-indigo-50 dark:hover:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 transition-colors"
                    >
                      <Edit2 className="size-3 hover:text-indigo-600" />
                    </button>
                  </Tip>
                )}
              </div>
            </div>
            {(comp as any).createdAt && (
              <div className="p-3 text-center">
                <div className="flex items-center justify-center gap-1 text-foreground mb-1.5">
                  <CalendarDays className="size-3" />
                  <span className="text-[10px] font-medium whitespace-nowrap">Added On</span>
                </div>
                <span className="text-[10px] font-semibold text-foreground">
                  {new Date((comp as any).createdAt).toLocaleDateString(
                    "en-US",
                    { month: "short", day: "2-digit", year: "numeric" },
                  )}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Notes (if any) */}
        {comp.notes && (
          <div className="rounded-xl border border-border bg-muted/10 px-4 py-1">
            <DetailRow
              icon={<Box className="size-3.5" />}
              label="Notes"
              value={comp.notes}
            />
          </div>
        )}

        {/* Connected Devices — 2-column grid */}
        {deployments.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-foreground">
                Connected {deployments.length === 1 ? "Device" : "Devices"}
                <span className="ml-1.5 d font-normal">
                  ({deployments.length})
                </span>
              </p>
              {!isService && (
                <button
                  onClick={() =>
                    isCloud ? onAddCloudResource(comp) : onAddDevice(comp)
                  }
                  className="flex items-center gap-1 px-2.5 py-1 rounded-lg border border-indigo-500/40 text-[11px] font-medium text-indigo-600 dark:text-indigo-400 hover:bg-indigo-500/10 transition-colors"
                >
                  <Plus className="size-3" />
                  {isCloud ? "Add Cloud Resource" : "Add Device"}
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {deployments.map((d: any) => {
                const isCloudDep = d.ciType === "CLOUD";
                const name = isCloudDep
                  ? d.cloudResourceName ||
                    d.ciName ||
                    d.cloudServiceName ||
                    "Cloud Resource"
                  : d.ciName;
                return (
                  <div
                    key={d.assetId}
                    className="rounded-xl border border-border bg-muted/10 overflow-hidden flex flex-col"
                  >
                    {/* Device header */}
                    <div className="flex items-center gap-2 px-3 py-2.5 border-b border-border/50">
                      <div className="flex-shrink-0 size-7 rounded-lg bg-slate-500/15 flex items-center justify-center">
                        {isCloudDep ? (
                          <Cloud className="size-3.5 text-indigo-400" />
                        ) : (
                          <Server className="size-3.5 text-slate-400" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <Tip label={name} side="top" className="block min-w-0">
                          <p className="font-semibold text-xs text-foreground truncate">
                            {name}
                          </p>
                        </Tip>
                        <StatusDot status={d.status} />
                      </div>
                    </div>

                    {/* Role badge */}
                    <div className="px-3 pt-2 pb-1">
                      <span
                        className={`text-[10px] font-bold px-2 py-0.5 rounded-md ${rolePillCls(d.role)}`}
                      >
                        {(d.role || "PRIMARY").charAt(0).toUpperCase() +
                          (d.role || "primary").slice(1).toLowerCase()}
                      </span>
                    </div>

                    {/* Detail rows */}
                    <div className="px-3 pb-1 flex-1">
                      {isCloudDep ? (
                        <>
                          <DeviceField
                            icon={<Globe className="size-3.5" />}
                            label="ID"
                            value={d.cloudResourceIdentifier || d.ciIdentifier}
                          />
                          <DeviceField
                            icon={<Layers className="size-3.5" />}
                            label="Service"
                            value={d.cloudServiceName || d.ciName}
                          />
                          <DeviceField
                            icon={<Globe className="size-3.5" />}
                            label="Region"
                            value={d.region}
                          />
                        </>
                      ) : (
                        <>
                          <DeviceField
                            icon={<Globe className="size-3.5" />}
                            label="IP"
                            value={d.ciIpAddress || d.ciIdentifier}
                          />
                          <DeviceField
                            icon={<Plug className="size-3.5" />}
                            label="Port"
                            value={d.port}
                          />
                          <DeviceField
                            icon={<Shield className="size-3.5" />}
                            label="Protocol"
                            value={d.protocol}
                          />
                          <DeviceField
                            icon={<Layers className="size-3.5" />}
                            label="Role"
                            value={d.role}
                          />
                        </>
                      )}
                    </div>

                    {/* Actions */}
                    {!isService && (
                      <div className="flex gap-1.5 px-3 pb-2.5 pt-1">
                        {!isCloudDep && (
                          <button
                            onClick={() => onEditDeployment(d)}
                            className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg bg-indigo-600/10 border border-indigo-500/30 text-indigo-400 hover:bg-indigo-600/20 text-[11px] font-medium transition-colors"
                          >
                            <Edit2 className="size-3" /> Edit
                          </button>
                        )}
                        <button
                          onClick={() => onRemoveDeployment(d)}
                          disabled={removingDeploymentIds[d.assetId]}
                          className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20 text-[11px] font-medium transition-colors disabled:opacity-50"
                        >
                          {removingDeploymentIds[d.assetId] ? (
                            <Loader className="size-3 animate-spin" />
                          ) : (
                            <>
                              <Trash2 className="size-3" /> Delete
                            </>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────

export function AppComponentTab({
  components,
  onRemoveComponent,
  onAddDevice,
  onAddCloudResource,
  onEditComponent,
  onEditDeployment,
  onRemoveDeployment,
  removingCompIds,
  removingDeploymentIds,
}: AppComponentTabProps) {
  const [selectedComp, setSelectedComp] =
    useState<AppComponentMappingResponse | null>(null);

  // Keep panel in sync if deployments update (e.g., after add/remove)
  useEffect(() => {
    if (!selectedComp) return;
    const updated = components.find((c) => c.id === selectedComp.id);
    if (updated) setSelectedComp(updated);
    else setSelectedComp(null);
  }, [components]);

  if (components.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3 text-center border border-dashed border-border rounded-xl bg-muted/10">
        <div className="size-12 rounded-xl bg-muted/60 flex items-center justify-center">
          <Server className="size-6 d/40" />
        </div>
        <p className="text-sm font-medium d">No components mapped yet</p>
        <p className="text-xs d/60">
          Add an on-prem component or link a cloud account to auto-discover.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col lg:flex-row gap-4 items-start">
      {/* Component list */}
      <div className="order-2 lg:order-1 flex-1 min-w-0 grid grid-cols-1 min-[1700px]:grid-cols-2 gap-2.5 lg:self-start">
        {components.map((comp) => {
          const isService = comp.source === "SERVICE";
          const isCloud = comp.source === "CLOUD";
          const deployments = comp.deployments ?? [];
          const iconClass = typeIconBg(
            comp.component.componentType,
            comp.source,
          );
          const effectiveSrc = getEffectiveSource(comp);
          const isSelected = selectedComp?.id === comp.id;

          return (
            <Tip
              key={comp.id}
              label={isSelected ? "Click to close details" : "Click to view details"}
              side="top"
              className="block w-full h-[94px]"
            >
            <div
              onClick={() => setSelectedComp(isSelected ? null : comp)}
              className={`group block w-full h-full rounded-xl border bg-card cursor-pointer transition-all overflow-hidden ${
                isSelected
                  ? "border-indigo-500/60 bg-indigo-500/5 shadow-[0_0_0_1px_rgba(99,102,241,0.2)]"
                  : "border-border hover:border-indigo-500/40 hover:bg-indigo-500/5 hover:shadow-md"
              }`}
            >
              <div className="flex items-center gap-3 px-4 py-3.5">
                <div
                  className={`flex-shrink-0 size-10 rounded-xl flex items-center justify-center transition-transform group-hover:scale-105 ${iconClass}`}
                >
                  {isCloud ? (
                    <Cloud className="size-5" />
                  ) : (
                    <Server className="size-5" />
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 min-w-0">
                    <Tip label={comp.component.title} side="top" className="min-w-0 block">
                      <span
                        className={`block font-semibold text-sm transition-colors truncate whitespace-nowrap min-w-0 ${isSelected ? "text-indigo-600 dark:text-indigo-400" : "text-foreground group-hover:text-indigo-600 dark:group-hover:text-indigo-400"}`}
                      >
                        {comp.component.title}
                      </span>
                    </Tip>
                    {comp.component.componentType && (
                      <Tip label={comp.component.componentType} side="top" className="flex-shrink-0 block">
                        <span className="block text-[10px] font-semibold bg-slate-100 dark:bg-muted text-slate-600 dark:text-slate-300 border border-slate-300 dark:border-slate-600 px-2 py-0.5 rounded-md tracking-wide whitespace-nowrap max-w-[120px] truncate">
                          {comp.component.componentType}
                        </span>
                      </Tip>
                    )}
                  </div>
                  <p className="text-xs d mt-0.5">
                    {deployments.length} asset
                    {deployments.length !== 1 ? "s" : ""}
                  </p>
                  {comp.sspLayerName && (
                    <div className="mt-1.5 flex items-center gap-1 text-[11px] font-medium text-violet-600 dark:text-violet-400">
                      <Layers className="size-3 flex-shrink-0" />
                      <span className="truncate" title={comp.sspLayerName}>
                        {comp.sspLayerName}
                      </span>
                    </div>
                  )}
                </div>

                <span
                  className={`flex-shrink-0 whitespace-nowrap text-[10px] font-medium text-indigo-600 dark:text-indigo-400 transition-all duration-200 ${
                    isSelected
                      ? "opacity-0 w-0 overflow-hidden"
                      : "opacity-0 group-hover:opacity-100"
                  }`}
                >
                  View details
                </span>

                <span
                  className={`flex-shrink-0 text-[10px] font-bold px-2.5 py-1 rounded-lg tracking-wide ${sourceBadgeCls(effectiveSrc)}`}
                >
                  {effectiveSrc}
                </span>

                {!isService && (
                  <span onClick={(e) => e.stopPropagation()}>
                    <MoreMenu
                      onRemove={() => onRemoveComponent(comp)}
                      removing={removingCompIds[comp.id]}
                    />
                  </span>
                )}
              </div>
            </div>
            </Tip>
          );
        })}
      </div>

      {/* Right panel — always visible */}
      <div
        className={`${selectedComp ? "flex" : "hidden lg:flex"} order-1 lg:order-2 w-full lg:w-[clamp(420px,38vw,640px)] flex-shrink-0 lg:sticky lg:top-4 rounded-2xl border border-border bg-card overflow-hidden flex-col max-h-[75vh] lg:max-h-[calc(100vh-2rem)]`}
      >
        {selectedComp ? (
          <ComponentDetailPanel
            comp={selectedComp}
            onClose={() => setSelectedComp(null)}
            onAddDevice={onAddDevice}
            onAddCloudResource={onAddCloudResource}
            onEditComponent={onEditComponent}
            onEditDeployment={onEditDeployment}
            onRemoveDeployment={onRemoveDeployment}
            removingDeploymentIds={removingDeploymentIds}
          />
        ) : (
          <div className="flex flex-col items-center justify-center h-full min-h-[320px] gap-3 px-8 text-center">
            <div className="size-16 rounded-2xl bg-indigo-500/15 flex items-center justify-center">
              <Layers className="size-7 text-indigo-400/70" />
            </div>
            <p className="text-base font-semibold text-foreground">
              Select a component
            </p>
            <p className="text-sm d">
              Click any component on the left to view its details here.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
